import { useEffect, useRef, useMemo } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import { DateSeparator } from './DateSeparator';
import { Skeleton } from '@/components/ui/skeleton';
import { useMessages } from '@/lib/hooks/use-messages';
import { useUser } from '@clerk/clerk-react';
import { useConversation } from '@/lib/hooks/use-conversations';
import { Sparkles } from 'lucide-react';
import { useUserPreferences } from '@/lib/hooks/use-user-preferences';
import { useQueryClient } from '@tanstack/react-query';
import type { Message } from '@/lib/api/services/chat.service';

interface ChatThreadProps {
  conversationId: string;
  otherParticipant?: {
    user_id: string;
    name?: string;
    avatar?: string;
    online?: boolean;
  };
  isTyping?: boolean;
}

// Component to render a message with its sender name
function MessageWithName({
  message,
  isOwn,
  isLastMessage,
  participantDisplayNames,
}: {
  message: Message;
  isOwn: boolean;
  isLastMessage: boolean;
  participantDisplayNames: Map<string, string | null>;
}) {
  // Use display name from conversation participant data instead of API call
  const senderName = participantDisplayNames.get(message.sender_id) || undefined;
  return (
    <MessageBubble
      message={message}
      isOwn={isOwn}
      senderName={senderName}
      isLastMessage={isLastMessage}
    />
  );
}

export function ChatThread({
  conversationId,
  otherParticipant,
  isTyping = false,
}: ChatThreadProps) {
  const { user } = useUser();
  const { conversation } = useConversation(conversationId);
  const { preferences, updatePreferences } = useUserPreferences();
  const queryClient = useQueryClient();
  const { messages, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useMessages(
    conversationId
  );
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Create a map of user IDs to display names from conversation participants
  const participantDisplayNames = useMemo(() => {
    const map = new Map<string, string | null>();
    if (conversation?.participants) {
      conversation.participants.forEach((participant) => {
        map.set(participant.user_id, participant.display_name || null);
      });
    }
    return map;
  }, [conversation?.participants]);

  // Group messages by date and insert date separators
  const messagesWithSeparators = useMemo(() => {
    if (!messages.length) return [];
    
    const result: Array<{ type: 'message'; message: Message; index: number } | { type: 'separator'; date: Date }> = [];
    let lastDate: Date | null = null;
    
    messages.forEach((message, index) => {
      const messageDate = new Date(message.created_at);
      messageDate.setHours(0, 0, 0, 0);
      
      // Check if we need a date separator
      if (!lastDate || messageDate.getTime() !== lastDate.getTime()) {
        result.push({ type: 'separator', date: new Date(message.created_at) });
        lastDate = messageDate;
      }
      
      result.push({ type: 'message', message, index });
    });
    
    return result;
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesContainerRef.current) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages.length]); // Only trigger on message count change

  // Auto-scroll to bottom when typing indicator appears
  useEffect(() => {
    if (isTyping && messagesContainerRef.current) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [isTyping]);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white">
      {/* Chat Header */}
      {otherParticipant && (
        <div className="flex-shrink-0 p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="h-10 w-10">
                {otherParticipant.avatar && (
                  <AvatarImage src={otherParticipant.avatar} alt={otherParticipant.name || 'User'} />
                )}
                <AvatarFallback className="font-semibold bg-slate-200 text-slate-600">
                  {otherParticipant.name
                    ?.split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) ||
                    'U'}
                </AvatarFallback>
              </Avatar>
              {otherParticipant.online && (
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 rounded-full border-white" />
              )}
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">
                {otherParticipant.name || 'User'}
              </h2>
              <div className="flex items-center gap-2">
                {otherParticipant.online && (
                  <>
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                    <span className="text-xs text-emerald-500">Online</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              const newCollapsedState = !preferences.helper_collapsed;
              
              // Optimistically update the UI immediately
              type UserPreferences = {
                id?: string;
                user_id?: string;
                tips_enabled: boolean;
                helper_collapsed: boolean;
                suggestions_collapsed: boolean;
                created_at?: string;
                updated_at?: string;
              };
              
              queryClient.setQueryData<UserPreferences>(['user-preferences'], (old) => ({
                ...(old || {
                  tips_enabled: true,
                  helper_collapsed: true,
                  suggestions_collapsed: true,
                }),
                helper_collapsed: newCollapsedState,
              }));
              
              // Save preference in the background (don't await)
              updatePreferences({ helper_collapsed: newCollapsedState }).catch((error) => {
                console.error('Failed to save helper preference:', error);
                // Revert on error
                queryClient.setQueryData<UserPreferences>(['user-preferences'], (old) => ({
                  ...(old || {
                    tips_enabled: true,
                    helper_collapsed: true,
                    suggestions_collapsed: true,
                  }),
                  helper_collapsed: !newCollapsedState,
                }));
              });
            }}
            className="h-12 bg-[#4669ed] text-white px-4 py-2 rounded-full shadow-lg hover:bg-[#2b42a3] transition flex items-center justify-center"
            title="Get Tips"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Get Tips</span>
          </button>
        </div>
      )}

      {/* Messages Area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 min-h-0">
        {hasNextPage && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="text-sm px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              {isFetchingNextPage ? 'Loading...' : 'Load older messages'}
            </button>
          </div>
        )}

        {isLoading && messages.length === 0 ? (
          <div className="space-y-4 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`flex gap-2 ${i % 3 === 0 ? 'justify-end' : 'justify-start'}`}
              >
                {i % 3 !== 0 && (
                  <Skeleton className="h-8 w-8 rounded-full" />
                )}
                <div className={`flex flex-col gap-1 ${i % 3 === 0 ? 'items-end' : 'items-start'}`}>
                  <Skeleton
                    className={`h-12 ${
                      i % 3 === 0
                        ? 'w-48 rounded-2xl rounded-br-md'
                        : i % 3 === 1
                        ? 'w-64 rounded-2xl rounded-bl-md'
                        : 'w-56 rounded-2xl rounded-bl-md'
                    }`}
                  />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1">
            {messagesWithSeparators.map((item) => {
              if (item.type === 'separator') {
                return <DateSeparator key={`separator-${item.date.getTime()}`} date={item.date} />;
              } else {
                return (
                  <MessageWithName
                    key={item.message.id}
                    message={item.message}
                    isOwn={item.message.sender_id === user?.id}
                    isLastMessage={item.index === messages.length - 1}
                    participantDisplayNames={participantDisplayNames}
                  />
                );
              }
            })}
          </div>
        )}

        {isTyping && (
          <TypingIndicator
            senderName={otherParticipant?.name}
            senderAvatar={otherParticipant?.avatar}
          />
        )}
      </div>
    </div>
  );
}
